import { type LoaderFunctionArgs, redirect } from "react-router";
import { BASE_PATH } from "src/basepath";
import { getToken } from "src/business-hooks/useToken";
import type { EnvResponse } from "src/queries/env/env.types";
import type { LicenseFeaturesResponse } from "src/queries/platform/license.types";
import { useGlobalLocalContext } from "src/storage/localStorage";
import { appRoutes } from "src/utils/appRoutes/appRoutes";
import { tokenAtom } from "src/utils/auth/auth-atoms";
import { fetchOutsideReact } from "src/utils/fetchOutsideReact";
import { AppStore } from "src/utils/jotai";
import { prefetchRouterLoader } from "./prefetchRouterLoader";

// the sidebar links do not include the "env" parameter as part of the link
// because the "env" is dynamic and fetched from the server, the links which require
// an "env" in their route needs to be redirected to include the (selected) "env" param.
export const envRedirectRouterLoader = async (
  args: LoaderFunctionArgs
): Promise<Response | boolean> => {
  // in case there is no token, do not procceed to the below logic which requires a token for various API calls
  // and is also completely irrelevant logic path for non-authenticated users
  if (!getToken()) {
    return false;
  }

  await prefetchRouterLoader(args);

  // Check if we're already on the welcome page to prevent redirect loops
  const url = new URL(args.request.url);
  const path = url.pathname.replace(BASE_PATH, "");

  const isSuperAdmin = appRoutes["super-admin"].matcher(path);
  const isWelcome = appRoutes.welcome.matcher(path);

  const token = AppStore.get(tokenAtom);

  if ((token?.is_super_admin && isSuperAdmin) || isWelcome) {
    return true;
  }

  // fetch the license as soon as possible, so it'll be cached by tanstack-query
  fetchOutsideReact<LicenseFeaturesResponse>({ url: "/api/license" });

  const envs = await fetchOutsideReact<EnvResponse[]>({
    url: "/api/envs",
    body: {},
  });

  if (!envs?.length) {
    console.warn("No envs returned from db");
    return redirect("/welcome");
  }

  // the below is the default route logic (when first logging in).
  // it selects the env with the most pipelines, and redirects to it, which leads to the "dashboard" route
  const envsNames = envs?.map((a) => a.env);
  const previousPath = args?.params["*"] || "";
  const storageEnv = useGlobalLocalContext.getState().env;
  const storageEnvExistsInEnvList = envsNames.includes(storageEnv);

  // if the URL already includes an "env" param
  if (args.params.env_name && envsNames.includes(args.params.env_name)) {
    return true;
  } else if (storageEnvExistsInEnvList) {
    return redirect(`/envs/${storageEnv}/${previousPath}`);
  } else {
    // Default to the env with the most pipelines (tie-break alphabetically)
    const defaultEnv = [...envs].sort(
      (a, b) => b.cnt - a.cnt || a.env.localeCompare(b.env)
    )[0].env;
    return redirect(`/envs/${defaultEnv}/${previousPath}`);
  }
};
